import { useEffect, useState, useRef } from "react";
import { Globe, Check } from "lucide-react";

const LANGS = [
  { code: "en", label: "English",   native: "English" },
  { code: "fr", label: "French",    native: "Français" },
  { code: "de", label: "German",    native: "Deutsch" },
  { code: "es", label: "Spanish",   native: "Español" },
  { code: "ru", label: "Russian",   native: "Русский" },
  { code: "sw", label: "Swahili",   native: "Kiswahili" },
  { code: "zh-CN", label: "Chinese", native: "中文" },
  { code: "ar", label: "Arabic",    native: "العربية" },
];

const STORAGE_KEY = "abj_lang";
const INCLUDED   = "en,fr,de,es,ru,sw,zh-CN,ar";

/**
 * Robust Google Translate switcher.
 *
 * Fixes the "blink + reset to English" bug by:
 *  1. Mounting the Google widget in a visible-but-clipped container (Google's
 *     TranslateElement does NOT initialise inside `display:none` nodes).
 *  2. Driving translation via the injected `.goog-te-combo` <select> —
 *     no page reload, no cookie race condition.
 *  3. Setting the `googtrans` cookie on the exact document domain AND its
 *     parent domain so it survives across route/reload and custom-domain hops.
 *  4. Watching route changes with a MutationObserver + polling
 *     to re-apply the stored language if React re-renders the DOM.
 */
export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(() => localStorage.getItem(STORAGE_KEY) || "en");
  const ref = useRef(null);
  const initedRef = useRef(false);

  // -- helpers --------------------------------------------------------------
  const setGoogTransCookie = (code) => {
    const value = code === "en" ? "" : `/en/${code}`;
    const expire = code === "en"
      ? "expires=Thu, 01 Jan 1970 00:00:00 GMT"
      : "max-age=31536000";
    const host = window.location.hostname;
    const parts = host.split(".");
    const rootDomain = parts.length > 1 ? parts.slice(-2).join(".") : host;
    // path=/ (this host)
    document.cookie = `googtrans=${value}; ${expire}; path=/;`;
    // exact hostname (some browsers keep these separate)
    document.cookie = `googtrans=${value}; ${expire}; path=/; domain=${host};`;
    // parent domain (covers subdomains like preview.emergentagent.com)
    document.cookie = `googtrans=${value}; ${expire}; path=/; domain=.${rootDomain};`;
  };

  const applyLang = (code) => {
    setGoogTransCookie(code);
    localStorage.setItem(STORAGE_KEY, code);
    setActive(code);
    setOpen(false);
    // Google Translate reads the `googtrans` cookie on page load and rewrites
    // the DOM before paint. The modern widget no longer exposes a stable
    // <select class="goog-te-combo"> to drive programmatically, so a full
    // reload is the reliable path for every language (including switching
    // back to English, which clears the cookie above).
    window.location.reload();
  };

  // -- init Google Translate script (once) ---------------------------------
  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;

    window.googleTranslateElementInit = () => {
      /* eslint-disable no-new */
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: INCLUDED,
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        "google_translate_element"
      );
      /* eslint-enable no-new */
    };

    const s = document.createElement("script");
    s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    s.defer = true;
    document.body.appendChild(s);

    // Hide the Google top-bar frame that pushes the page down + tooltip
    const style = document.createElement("style");
    style.innerHTML = `
      .skiptranslate, iframe.goog-te-banner-frame, .goog-te-banner-frame { display: none !important; }
      body { top: 0 !important; position: static !important; }
      .goog-te-gadget { font-size: 0 !important; height: 0; overflow: hidden; }
      .goog-te-gadget > span, .goog-te-gadget > div > span { display: none !important; }
      .goog-tooltip, .goog-tooltip:hover { display: none !important; }
      .goog-text-highlight { background: none !important; box-shadow: none !important; }
    `;
    document.head.appendChild(style);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const activeLang = LANGS.find((l) => l.code === active) || LANGS[0];

  return (
    <>
      {/*
        Google Translate mount MUST be visible in the render tree to initialise.
        We clip it off-screen so the crude default UI is invisible while our
        custom dropdown drives the injected <select> programmatically.
      */}
      <div
        id="google_translate_element"
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
        }}
      />

      <div ref={ref} className="relative notranslate" translate="no" data-testid="lang-switcher">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-label="Change language"
          data-testid="lang-btn"
          className="inline-flex items-center gap-1.5 p-2 text-white/80 hover:text-gold-300 transition-colors text-xs uppercase tracking-[0.15em]"
        >
          <Globe size={16} />
          <span className="hidden sm:inline">{activeLang.native}</span>
        </button>
        {open && (
          <div
            className="absolute right-0 top-full mt-2 min-w-[190px] bg-ink border border-gold-500/30 shadow-xl py-2 z-[90]"
            data-testid="lang-menu"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => applyLang(l.code)}
                data-testid={`lang-${l.code}`}
                className={`w-full flex items-center justify-between gap-4 px-4 py-2 text-sm text-left transition-colors ${
                  active === l.code ? "text-gold-300 bg-gold-500/10" : "text-white/80 hover:text-gold-300 hover:bg-white/5"
                }`}
              >
                <span>
                  <span className="font-medium">{l.native}</span>
                  <span className="text-white/40 text-xs ml-2">{l.label}</span>
                </span>
                {active === l.code && <Check size={13} className="text-gold-400" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
